import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Palette } from 'Palette';
import { StyledCard, StyledTypography } from '../../styles/styles';

const ENTITY_TYPE_LABELS: Record<string, string> = {
  TRUST: 'Trust',
  COMPANY: 'Company',
  PARTNERSHIP: 'Partnership',
  FUND: 'Fund',
  SMSF: 'SMSF',
  OTHER: 'Other',
};

const CONTACT_ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  BENEFICIARY: 'Beneficiary',
  TRUSTEE: 'Trustee',
  DIRECTOR: 'Director',
  PARTNER: 'Partner',
  AUTHORIZED_REPRESENTATIVE: 'Authorized Representative',
  CONTACT_PERSON: 'Contact Person',
};

function getEntityIcon(type: string): string {
  switch (type) {
    case 'TRUST':
      return '🏛️';
    case 'COMPANY':
      return '🏢';
    case 'PARTNERSHIP':
      return '🤝';
    case 'FUND':
      return '💰';
    case 'SMSF':
      return '🏦';
    default:
      return '📁';
  }
}

export interface InvestmentEntityPipelineCardProps {
  entityId: string;
  entityName: string;
  entityType?: string;
  contactRole?: string;
  description?: string;
  tone?: keyof typeof Palette;
  entityNavigateUrl?: string;
  onPreviewNavigate?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Kanban column height; pass false for content-sized cards (e.g. chat popover). */
  fixedHeight?: string | false;
  /** Chat popover: kanban hover overlay with View Entity only. */
  previewMode?: boolean;
}

const InvestmentEntityPipelineCard: React.FC<InvestmentEntityPipelineCardProps> = ({
  entityId,
  entityName,
  entityType = 'OTHER',
  contactRole,
  description,
  tone = 'Primary',
  entityNavigateUrl,
  onPreviewNavigate,
  onEdit,
  onDelete,
  fixedHeight = '140px',
  previewMode = false,
}) => {
  const navigate = useNavigate();
  const showEntityPreviewOverlay = previewMode;
  const showKanbanOverlay = !previewMode && Boolean(onEdit || onDelete);
  const showPipelineOverlay = showEntityPreviewOverlay || showKanbanOverlay;
  const useCompactHeight = previewMode || fixedHeight === false;

  const handleCardClick = () => {
    if (previewMode) {
      return;
    }
    navigate(entityNavigateUrl ?? `/investment-entities/${entityId}`);
  };

  const handleViewEntity = (event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(entityNavigateUrl ?? `/investment-entities/${entityId}`);
    if (previewMode) {
      onPreviewNavigate?.();
    }
  };

  const typeLabel = ENTITY_TYPE_LABELS[entityType] ?? entityType.replace(/_/g, ' ');
  const roleLabel = contactRole
    ? (CONTACT_ROLE_LABELS[contactRole] ?? contactRole.replace(/_/g, ' '))
    : undefined;

  return (
    <StyledCard
      tone={tone}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        ...(useCompactHeight
          ? { height: 'auto', flex: '0 0 auto' }
          : { height: fixedHeight, flexShrink: 0 }),
        width: '200px',
        margin: previewMode ? 0 : '0 8px 12px 0',
        overflow: 'hidden',
        ...(showPipelineOverlay && {
          '&:hover .action-overlay': {
            opacity: 1,
            visibility: 'visible',
          },
        }),
      }}
      onClick={handleCardClick}
    >
      <Stack spacing={0.5} sx={{ p: 1, ...(useCompactHeight ? {} : { height: '100%' }) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '1.2rem' }}>{getEntityIcon(entityType)}</Typography>
          <StyledTypography
            tone={tone}
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.1,
              flex: 1,
            }}
          >
            {entityName || 'Unnamed Entity'}
          </StyledTypography>
        </Box>

        <StyledTypography
          tone={tone}
          sx={{
            fontSize: '0.55rem',
            opacity: 0.8,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {typeLabel}
        </StyledTypography>

        {roleLabel && (
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              fontSize: '0.45rem',
              height: '18px',
              backgroundColor: 'rgba(0,0,0,0.1)',
              color: 'inherit',
            }}
          />
        )}

        {description && (
          <StyledTypography
            tone={tone}
            sx={{
              fontSize: '0.45rem',
              opacity: 0.7,
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {description}
          </StyledTypography>
        )}
      </Stack>

      {showPipelineOverlay && (
        <Box
          className="action-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease',
            zIndex: 10,
          }}
        >
          <StyledTypography
            sx={{
              color: 'white',
              fontSize: '0.6rem',
              fontWeight: 700,
              marginBottom: '6px',
              textAlign: 'center',
              lineHeight: 1.1,
            }}
          >
            {entityName || 'Unnamed Entity'}
          </StyledTypography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="View Entity">
              <IconButton
                aria-label="View Entity"
                onClick={handleViewEntity}
                sx={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <VisibilityIcon sx={{ fontSize: '12px' }} />
              </IconButton>
            </Tooltip>
            {!previewMode && onEdit && (
              <Tooltip title="Edit Association">
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onEdit();
                  }}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <EditIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              </Tooltip>
            )}
            {!previewMode && onDelete && (
              <Tooltip title="Remove Association">
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete();
                  }}
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: '12px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}
    </StyledCard>
  );
};

export default InvestmentEntityPipelineCard;
